import type {
  WeatherLayerSeries,
  WeatherLayerSeriesStats,
} from "../types/api";

type Series = WeatherLayerSeries | WeatherLayerSeriesStats;

function isZonal(s: Series): s is WeatherLayerSeriesStats {
  return "min" in s && "max" in s;
}

function fmt(n: number | null | undefined, digits = 2) {
  return n == null ? "—" : n.toFixed(digits);
}

/**
 * Weather table for the multi-year case — one column per requested year, plus a
 * Mean column (or, for zonal stats, a min/max/mean row block per layer).
 */
export function WeatherSeriesTable({ rows }: { rows: Series[] }) {
  if (!rows.length) {
    return (
      <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
        <h3 className="mef-caption">Weather</h3>
        <p className="mt-2 text-[13px] leading-4 text-black/40">No weather layers ingested yet.</p>
      </div>
    );
  }

  const years = rows[0]?.per_year.map((p) => p.year) ?? [];
  const zonal = rows.some(isZonal);

  return (
    <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 shadow-mef-card">
      <h3 className="mef-caption">
        Weather — {years.length}-year {zonal ? "zonal stats" : "series"}
      </h3>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left mef-small-caption">
              <th className="py-1 pr-3 font-medium">Layer</th>
              {zonal && <th className="py-1 pr-2 font-medium">Stat</th>}
              {years.map((y) => (
                <th key={y} className="py-1 px-2 text-right font-medium tabular-nums">
                  {y}
                </th>
              ))}
              <th className="py-1 pl-3 text-right font-medium border-l border-mef-surface-2">
                Mean
              </th>
              <th className="py-1 pl-3 font-medium">Unit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) =>
              isZonal(r) ? (
                <ZonalLayerRows key={r.code} row={r} years={years} />
              ) : (
                <tr key={r.code} className="border-t border-mef-surface-2">
                  <td className="py-1.5 pr-3 font-mono text-black/80">{r.code}</td>
                  {r.per_year.map((p) => (
                    <td
                      key={p.year}
                      className="py-1.5 px-2 text-right tabular-nums text-black/80"
                    >
                      {fmt(p.value)}
                    </td>
                  ))}
                  <td className="py-1.5 pl-3 text-right tabular-nums text-black/80 font-medium border-l border-mef-surface-2">
                    {fmt(r.mean)}
                  </td>
                  <td className="py-1.5 pl-3 text-black/40">{r.unit ?? ""}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ZonalLayerRows({
  row,
  years,
}: {
  row: WeatherLayerSeriesStats;
  years: number[];
}) {
  const stats = [
    { key: "min", label: "min", agg: row.min },
    { key: "mean", label: "mean", agg: row.mean },
    { key: "max", label: "max", agg: row.max },
  ] as const;
  return (
    <>
      {stats.map((s, i) => (
        <tr
          key={`${row.code}-${s.key}`}
          className={i === 0 ? "border-t border-mef-separator" : "border-t border-mef-surface-2"}
        >
          {i === 0 ? (
            <td className="py-1.5 pr-3 font-mono text-black/80" rowSpan={3}>
              {row.code}
            </td>
          ) : null}
          <td className="py-1.5 pr-2 mef-small-caption">{s.label}</td>
          {years.map((y) => {
            const py = row.per_year.find((p) => p.year === y);
            return (
              <td
                key={y}
                className="py-1.5 px-2 text-right tabular-nums text-black/80"
              >
                {fmt(py?.[s.key as "min" | "mean" | "max"])}
              </td>
            );
          })}
          <td className="py-1.5 pl-3 text-right tabular-nums text-black/80 font-medium border-l border-mef-surface-2">
            {fmt(s.agg)}
          </td>
          {i === 0 ? (
            <td className="py-1.5 pl-3 text-black/40" rowSpan={3}>
              {row.unit ?? ""}
            </td>
          ) : null}
        </tr>
      ))}
    </>
  );
}
